import type { CountryLocalizations } from '../locale/types';

export const Countries = {
  Norway: 'Norway',
  Sweden: 'Sweden',
  Denmark: 'Denmark',
  Finland: 'Finland',
  Greenland: 'Greenland',
} as const;

export type Countries = (typeof Countries)[keyof typeof Countries];

export interface Country {
  readonly country: Countries;
  readonly name: (locale: CountryLocalizations) => string;
  readonly countryCode: '45' | '46' | '47' | '358' | '299';
  readonly regex: string;
  readonly minlength: number;
  readonly maxlength: number;
  toMsisdn: (n?: string) => string;
  formatWithCountryCode: (n?: string) => string;
  format: (n?: string) => string;
}

const Norway: Country = {
  country: Countries.Norway,
  name: (locale) => locale.norway.country,
  countryCode: '47',
  regex: '^[49]\\d{7}$',
  minlength: 8,
  maxlength: 8,
  toMsisdn(n) {
    return n && isValidForCountry(this, n) ? `${this.countryCode}${n}` : '';
  },
  formatWithCountryCode(n) {
    return `+${this.countryCode} ${this.format(n)}`;
  },
  format(n = '') {
    const formatName =
      /(?<firstPair>\d{1,2})?(?<secondPair>\d{1,2})?(?<thirdPair>\d{1,2})?(?<fourthPair>\d{1,2})?/;
    return formatNumberFromRegexMatch(n.match(formatName));
  },
};

const Sweden: Country = {
  country: Countries.Sweden,
  name: (locale) => locale.sweden.country,
  countryCode: '46',
  regex: '^7\\d{8}$',
  minlength: 9,
  maxlength: 9,
  toMsisdn(n) {
    return n && isValidForCountry(this, n) ? `${this.countryCode}${n}` : '';
  },
  formatWithCountryCode(n) {
    const prettyPrint = this.format(n);
    return `+${this.countryCode} ${prettyPrint}`;
  },
  format(n = '') {
    const formatName = /(?<firstGroup>\d{1,2})?(?<secondPair>\d{1,4})?(?<rest>\d*)?/;
    return formatNumberFromRegexMatch(n.match(formatName));
  },
};

const Denmark: Country = {
  country: Countries.Denmark,
  name: (locale) => locale.denmark.country,
  countryCode: '45',
  regex: '^\\d{8}$',
  minlength: 8,
  maxlength: 8,
  toMsisdn(n) {
    return n && isValidForCountry(this, n) ? `${this.countryCode}${n}` : '';
  },
  formatWithCountryCode(n) {
    return `+${this.countryCode} ${this.format(n)}`;
  },
  format(n = '') {
    const formatName =
      /(?<firstPair>\d{1,2})?(?<secondPair>\d{1,2})?(?<thirdPair>\d{1,2})?(?<fourthPair>\d{1,2})?/;
    return formatNumberFromRegexMatch(n.match(formatName));
  },
};

const Finland: Country = {
  country: Countries.Finland,
  name: (locale) => locale.finland.country,
  countryCode: '358',
  regex: '^[0-9]*$',
  minlength: 5,
  maxlength: 16,
  toMsisdn(n) {
    return n && isValidForCountry(this, n) ? `${this.countryCode}${n}` : '';
  },
  formatWithCountryCode(n) {
    const prettyPrint = this.format(n);
    return `+${this.countryCode} ${prettyPrint}`;
  },
  format(n = '') {
    const formatName =
      /(?<firstGroup>\d{1,2})?(?<secondGroup>\d{1,3})?(?<thirdGroup>\d{1,2})?(?<rest>\d*)?/;
    return formatNumberFromRegexMatch(n.match(formatName));
  },
};

const Greenland: Country = {
  country: Countries.Greenland,
  name: (locale) => locale.greenland.country,
  countryCode: '299',
  regex: '^\\d{6}$',
  minlength: 6,
  maxlength: 6,
  toMsisdn(n) {
    return n && isValidForCountry(this, n) ? `${this.countryCode}${n}` : '';
  },
  formatWithCountryCode(n) {
    const prettyPrint = this.format(n);
    return `+${this.countryCode} ${prettyPrint}`;
  },
  format(n = '') {
    const formatName =
      /(?<firstGroup>\d{1,2})?(?<secondGroup>\d{1,3})?(?<thirdGroup>\d{1,2})?(?<rest>\d*)?/;
    return formatNumberFromRegexMatch(n.match(formatName));
  },
};

export const CountryMap: { [key in Countries]: Country } = {
  Norway: Norway,
  Sweden: Sweden,
  Denmark: Denmark,
  Finland: Finland,
  Greenland: Greenland,
};

export interface MSISDN {
  readonly country: Country;
  number?: string;
}

const nonBreakingWhitespace = ' ';
function formatNumberFromRegexMatch(match: RegExpMatchArray | null): string {
  return (match || [])
    .slice(1) // remove first match group
    .filter((s) => !!s)
    .reduce((acc, curr, i) => acc + (i === 0 ? '' : nonBreakingWhitespace) + curr, '');
}

export function isValidMsisdn(m: MSISDN): boolean {
  return isValidForCountry(m.country, m.number);
}

export function msisdnToString(m: MSISDN): string {
  return m.country.toMsisdn(m.number);
}

function isValidForCountry(c: Country, n?: string): boolean {
  return (
    n !== undefined &&
    n?.length >= c.minlength &&
    n?.length <= c.maxlength &&
    new RegExp(c.regex).test(n)
  );
}

export function parseMsisdn(defaultCountry: Countries, msisdn?: string): MSISDN {
  const defaultMsisdn = {
    country: CountryMap[defaultCountry],
    number: undefined,
  };
  if (!msisdn) {
    return defaultMsisdn;
  }
  const msisdnWithoutWhitespace = msisdn.replace(/\s/g, '');
  let country = Object.values(CountryMap).find(
    (c) =>
      msisdnWithoutWhitespace.startsWith(c.countryCode) &&
      new RegExp(c.regex).test(msisdnWithoutWhitespace.slice(c.countryCode.length))
  );

  // Handle DWO only sending a 8 digit number for Norway
  // We will get a prefix from epayments (e.g. 4712345678) and in this case country will be defined
  if (country === undefined && msisdnWithoutWhitespace.length === 8) {
    country = Norway;
  }
  return country ? parseMsisdnForCountry(country.country, msisdn) : defaultMsisdn;
}

export function parseMsisdnForCountry(country: Countries, initialNumber?: string): MSISDN {
  const c = CountryMap[country];
  const msisdn: MSISDN = {
    country: c,
    number:
      (
        (initialNumber?.length || -1) >= c.minlength + c.countryCode.length &&
        initialNumber?.startsWith(c.countryCode)
      ) ?
        initialNumber?.slice(c.countryCode.length)
      : initialNumber,
  };
  if (!isValidMsisdn(msisdn)) {
    msisdn.number = '';
  }

  return msisdn;
}

export function prettyPrintMsisdn(msisdn: MSISDN): string {
  return msisdn.country.formatWithCountryCode(msisdn.number);
}
